import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useAuth } from "../store";

// 增长活动 P0 后台：概览 / 存入与退款队列 / 邀请关系 / 孤儿转账。
// 设计见 docs/qvx-growth-dev-plan-2026-11.md §6。

type GrowthTrial = {
  id: string;
  user_id: string;
  wallet_addr: string;
  username: string | null;
  status: string;
  principal: string;
  principal_units: string;
  tx_hash: string | null;
  refund_tx_hash: string | null;
  fail_reason: string | null;
  confirmed_at: string | null;
  unlock_at: string | null;
  refund_requested_at: string | null;
  refunded_at: string | null;
  created_at: string;
};

type GrowthReferral = {
  invitee_id: string;
  invitee_wallet: string;
  inviter_id: string;
  inviter_wallet: string;
  code: string;
  source: string;
  reward_eligible: boolean;
  ineligible_reason: string | null;
  bound_at: string;
};

type GrowthSummary = {
  config: { enabled: boolean; allow_wallets: string[]; trial: { treasury: string; symbol: string; amount: string } };
  trial_ready: boolean;
  referrals_total: number;
  referrals_today: number;
  trials_active: number;
  trials_refund_queue: number;
  trials_refunded: number;
  trials_pending: number;
  trials_problem: number;
  orphan_deposits: number;
  reconciliation_statuses?: { status: string; checked_at: string; payload: { treasury?: string; token?: string; balance_units?: string; outstanding_units?: string } }[];
  outstanding?: string;
  treasury_balance?: string;
  treasury_sufficient?: boolean;
  treasury_balance_error?: string;
  last_reconciliation?: { status: string; checked_at: string; payload: { balance_units?: string; outstanding_units?: string } };
};

type Orphan = { tx_hash: string; user_id: string | null; payload: { wallet?: string; amount_units?: string }; created_at: string };

const STATUS: Record<string, { color: string; label: string }> = {
  intent: { color: "default", label: "待付款" },
  submitted: { color: "processing", label: "确认中" },
  active: { color: "green", label: "体验中" },
  refund_requested: { color: "orange", label: "待退款" },
  refunded: { color: "blue", label: "已退款" },
  underpaid: { color: "red", label: "金额不足" },
  failed: { color: "red", label: "失败" },
  expired: { color: "default", label: "已过期" },
};

const scan = (hash: string) => `https://bscscan.com/tx/${hash}`;
const card = { background: "#10131A", border: "1px solid #1A1D26" };
const fmt = (s: string | null) => (s ? new Date(s).toLocaleString() : "—");

function Summary() {
  const { data, isLoading } = useQuery({
    queryKey: ["growth-summary"],
    queryFn: async () => (await api.get<GrowthSummary>("/growth/summary")).data,
    refetchInterval: 30_000,
  });
  if (isLoading || !data) return <Card style={card} loading />;
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {!data.config.enabled && <Alert type="info" showIcon message="活动未开启（system_settings: growth.config.enabled=false）" />}
      {data.config.enabled && data.config.allow_wallets?.length > 0 && (
        <Alert type="warning" showIcon message={`白名单模式：仅 ${data.config.allow_wallets.length} 个钱包可见`} />
      )}
      {data.config.enabled && !data.config.allow_wallets?.length && (
        <Alert type="error" showIcon message="allow_wallets 为空：活动对所有登录钱包可见；P0 试点请先配置白名单" />
      )}
      {data.config.enabled && !data.trial_ready && (
        <Alert type="warning" showIcon message="10 USDT 存入未开放：请检查 trial 配置与收款地址" />
      )}
      {data.treasury_sufficient === false && (
        <Alert type="error" showIcon message={`收款地址余额 ${data.treasury_balance} 低于未退本金 ${data.outstanding}，请立即核查`} />
      )}
      {data.reconciliation_statuses?.filter((r) => r.status === "treasury_shortfall").map((r) => (
        <Alert key={`${r.payload.treasury}-${r.payload.token}`} type="error" showIcon
          message={`收款地址 ${r.payload.treasury ?? "—"} 存在短款（${fmt(r.checked_at)}）：链上余额 ${r.payload.balance_units ?? "—"}，未退本金 ${r.payload.outstanding_units ?? "—"}（最小单位）`} />
      ))}
      {data.treasury_balance_error && <Alert type="warning" showIcon message={`收款地址余额读取失败：${data.treasury_balance_error}`} />}
      <Row gutter={16}>
        <Col span={4}><Card style={card}><Statistic title="邀请绑定（累计）" value={data.referrals_total} /></Card></Col>
        <Col span={4}><Card style={card}><Statistic title="今日绑定" value={data.referrals_today} /></Card></Col>
        <Col span={4}><Card style={card}><Statistic title="体验中" value={data.trials_active} /></Card></Col>
        <Col span={4}><Card style={card}><Statistic title="待退款" value={data.trials_refund_queue} valueStyle={{ color: data.trials_refund_queue ? "#FAA83B" : undefined }} /></Card></Col>
        <Col span={4}><Card style={card}><Statistic title="已退款" value={data.trials_refunded} /></Card></Col>
        <Col span={4}><Card style={card}><Statistic title="异常 / 孤儿转账" value={`${data.trials_problem} / ${data.orphan_deposits}`} /></Card></Col>
      </Row>
      <Card style={card}>
        <Typography.Text style={{ color: "#9CA3AF" }}>
          收款地址：<Typography.Text copyable style={{ color: "#F5F5F7" }}>{data.config.trial.treasury || "未配置"}</Typography.Text>
          　未退本金：{data.outstanding ?? "—"} {data.config.trial.symbol}
          　链上余额：{data.treasury_balance ?? "—"} {data.config.trial.symbol}
        </Typography.Text>
      </Card>
    </Space>
  );
}

function Trials() {
  const [status, setStatus] = useState("refund_queue");
  const [q, setQ] = useState("");
  const [refunding, setRefunding] = useState<GrowthTrial | null>(null);
  const [txHash, setTxHash] = useState("");
  const role = useAuth((s) => s.role);
  const canRefund = role === "super_admin" || role === "finance";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["growth-trials", status, q],
    queryFn: async () => (await api.get<{ items: GrowthTrial[] }>("/growth/trials", { params: { status, q, limit: 200 } })).data.items,
  });
  const refund = useMutation({
    mutationFn: ({ id, tx }: { id: string; tx: string }) => api.post(`/growth/trials/${id}/refund`, { tx_hash: tx }),
    onSuccess: () => {
      message.success("退款已核验并完成");
      setRefunding(null);
      setTxHash("");
      qc.invalidateQueries({ queryKey: ["growth-trials"] });
      qc.invalidateQueries({ queryKey: ["growth-summary"] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "核验失败"),
  });
  return (
    <Card
      style={card}
      title={<span style={{ color: "#F5F5F7" }}>10U 存入</span>}
      extra={
        <Space>
          <Input.Search allowClear placeholder="钱包地址 / 交易哈希 / ID" onSearch={(v) => setQ(v.trim())} style={{ width: 300 }} />
          <Select
            value={status}
            style={{ width: 140 }}
            onChange={setStatus}
            options={[
              { value: "refund_queue", label: "待退款" },
              { value: "active", label: "体验中" },
              { value: "pending", label: "待付款/确认中" },
              { value: "problem", label: "金额不足/失败" },
              { value: "refunded", label: "已退款" },
              { value: "all", label: "全部" },
            ]}
          />
        </Space>
      }
    >
      <Table<GrowthTrial>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1300 }}
        columns={[
          { title: "钱包", dataIndex: "wallet_addr", width: 360, render: (v: string, r) => <Space direction="vertical" size={0}><Typography.Text copyable style={{ color: "#F5F5F7" }}>{v}</Typography.Text>{r.username && <span style={{ color: "#6B7280" }}>{r.username}</span>}</Space> },
          { title: "状态", dataIndex: "status", width: 110, render: (v: string, r) => <Space direction="vertical" size={0}><Tag color={STATUS[v]?.color}>{STATUS[v]?.label ?? v}</Tag>{r.fail_reason && <span style={{ color: "#EF455A", fontSize: 12 }}>{r.fail_reason}</span>}</Space> },
          { title: "本金", dataIndex: "principal", width: 100 },
          { title: "存入交易", dataIndex: "tx_hash", width: 140, render: (v: string | null) => (v ? <a href={scan(v)} target="_blank" rel="noreferrer">{v.slice(0, 10)}…</a> : "—") },
          { title: "确认时间", dataIndex: "confirmed_at", width: 170, render: fmt },
          { title: "可取回", dataIndex: "unlock_at", width: 170, render: fmt },
          { title: "申请取回", dataIndex: "refund_requested_at", width: 170, render: fmt },
          { title: "退款交易", dataIndex: "refund_tx_hash", width: 140, render: (v: string | null) => (v ? <a href={scan(v)} target="_blank" rel="noreferrer">{v.slice(0, 10)}…</a> : "—") },
          {
            title: "操作",
            fixed: "right",
            width: 120,
            render: (_: unknown, r) =>
              r.status === "refund_requested" ? (
                <Button size="small" type="primary" disabled={!canRefund} onClick={() => setRefunding(r)}>
                  录入退款
                </Button>
              ) : null,
          },
        ]}
      />
      <Modal
        open={!!refunding}
        title="录入退款交易"
        okText="核验并完成"
        confirmLoading={refund.isPending}
        onCancel={() => { setRefunding(null); setTxHash(""); }}
        onOk={() => refunding && refund.mutate({ id: refunding.id, tx: txHash.trim() })}
      >
        {refunding && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              type="info"
              showIcon
              message={`请从收款地址向 ${refunding.wallet_addr} 转出 ${refunding.principal} USDT（BSC），上链确认后粘贴交易哈希。后端会核验 Transfer(收款地址 → 该钱包, ≥ 本金) 与确认数。`}
            />
            <Input placeholder="0x…（66 位）" value={txHash} onChange={(e) => setTxHash(e.target.value)} />
          </Space>
        )}
      </Modal>
    </Card>
  );
}

function Referrals() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["growth-referrals", q],
    queryFn: async () => (await api.get<{ items: GrowthReferral[] }>("/growth/referrals", { params: { q, limit: 200 } })).data.items,
  });
  return (
    <Card style={card} title={<span style={{ color: "#F5F5F7" }}>邀请关系</span>}
      extra={<Input.Search allowClear placeholder="钱包地址 / 邀请码" onSearch={(v) => setQ(v.trim())} style={{ width: 300 }} />}>
      <Table<GrowthReferral>
        rowKey="invitee_id"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1100 }}
        columns={[
          { title: "被邀请人", dataIndex: "invitee_wallet", width: 360 },
          { title: "邀请人", dataIndex: "inviter_wallet", width: 360 },
          { title: "邀请码", dataIndex: "code", width: 110 },
          { title: "来源", dataIndex: "source", width: 90 },
          { title: "计奖", dataIndex: "reward_eligible", width: 140, render: (v: boolean, r) => (v ? <Tag color="green">是</Tag> : <Tag color="red">否 · {r.ineligible_reason}</Tag>) },
          { title: "绑定时间", dataIndex: "bound_at", width: 170, render: fmt },
        ]}
      />
    </Card>
  );
}

function Orphans() {
  const [selected, setSelected] = useState<Orphan | null>(null);
  const [refundTxHash, setRefundTxHash] = useState("");
  const role = useAuth((s) => s.role);
  const canRefund = role === "super_admin" || role === "finance";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["growth-orphans"],
    queryFn: async () => (await api.get<{ items: Orphan[] }>("/growth/orphans")).data.items,
  });
  const resolve = useMutation({
    mutationFn: ({ orphan, refund }: { orphan: string; refund: string }) =>
      api.post(`/growth/orphans/${orphan}/refund`, { tx_hash: refund }),
    onSuccess: () => {
      message.success("孤儿转账退款已核验并归档");
      setSelected(null);
      setRefundTxHash("");
      qc.invalidateQueries({ queryKey: ["growth-orphans"] });
      qc.invalidateQueries({ queryKey: ["growth-summary"] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error ?? "退款核验失败"),
  });
  return (
    <Card style={card} title={<span style={{ color: "#F5F5F7" }}>孤儿转账</span>}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="没有对应活动记录的 USDT 转入（包括非平台用户钱包）。先从活动地址向原转出钱包退款，再提交已确认的退款交易；系统核验金额、收款钱包和确认数后归档并写入审计。" />
      <Table<Orphan>
        rowKey="tx_hash"
        loading={isLoading}
        dataSource={data}
        pagination={{ pageSize: 20 }}
        columns={[
          { title: "交易", dataIndex: "tx_hash", render: (v: string) => <a href={scan(v)} target="_blank" rel="noreferrer">{v}</a> },
          { title: "转出钱包", render: (_: unknown, r) => r.payload?.wallet ?? "—" },
          { title: "金额(最小单位)", render: (_: unknown, r) => r.payload?.amount_units ?? "—" },
          { title: "发现时间", dataIndex: "created_at", render: fmt },
          { title: "处理", fixed: "right", render: (_: unknown, r) => <Button size="small" type="primary" disabled={!canRefund} onClick={() => setSelected(r)}>录入退款</Button> },
        ]}
      />
      <Modal
        open={!!selected}
        title="核验孤儿转账退款"
        okText="核验并归档"
        confirmLoading={resolve.isPending}
        onCancel={() => { setSelected(null); setRefundTxHash(""); }}
        onOk={() => selected && resolve.mutate({ orphan: selected.tx_hash, refund: refundTxHash.trim() })}
      >
        {selected && <Space direction="vertical" style={{ width: "100%" }}>
          <Alert type="warning" showIcon message={`退款必须从活动收款地址转回 ${selected.payload.wallet ?? "原钱包"}，金额不得低于 ${selected.payload.amount_units ?? "原存入金额"} 个最小单位，并达到链上确认数。`} />
          <Input placeholder="已确认的 BSC 退款交易哈希" value={refundTxHash} onChange={(e) => setRefundTxHash(e.target.value)} />
        </Space>}
      </Modal>
    </Card>
  );
}

export default function Growth() {
  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Summary />
      <Tabs
        items={[
          { key: "trials", label: "10U 存入 / 退款", children: <Trials /> },
          { key: "referrals", label: "邀请关系", children: <Referrals /> },
          { key: "orphans", label: "孤儿转账", children: <Orphans /> },
        ]}
      />
    </Space>
  );
}
